import React, { useEffect } from "react";
import styled from "styled-components";
import { Button } from "./Button";
import { darken } from "polished";

const CameraContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FullscreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  z-index: 1000;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CapturedImage = styled.img`
  width: 100%;
  min-height: 350px;
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const PlaceholderBox = styled.div`
  width: 100%;
  min-height: 300px;
  background-color: ${({ theme }) => darken(0.04, theme.colors.background)};
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    display: none;
  }
`;

const PlaceholderText = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 1rem;
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md};
  opacity: 0.6;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: ${({ theme }) => theme.spacing.sm};
  width: 100%;
`;

const CaptureButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 1001;
`;

const CircularCaptureButton = styled.button`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: white;
  border: 4px solid rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    background-color: #f0f0f0;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 18px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

interface CameraPreviewProps {
  cameraActive: boolean;
  capturedImage: string | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onCapture: () => void;
  onAnalyze: () => void;
  onStartCamera: () => void;
  onFileUpload: () => void;
  setCameraActive: (active: boolean) => void;
  setCapturedImage: (image: string | null) => void;
  setDeviceInfo: (info: any | null) => void;
  resetScanner?: () => void;
  isConverting?: boolean;
}

export const CameraPreview: React.FC<CameraPreviewProps> = ({
  cameraActive,
  capturedImage,
  videoRef,
  canvasRef,
  onCapture,
  onAnalyze,
  onStartCamera,
  onFileUpload,
  setCameraActive,
  setCapturedImage,
  setDeviceInfo,
  resetScanner,
  isConverting = false,
}) => {
  const onReset = () => {
    if (resetScanner) {
      resetScanner();
    } else {
      setCapturedImage(null);
      setCameraActive(false);
      setDeviceInfo(null);
    }
  };

  // Prevent scrolling when camera is active
  useEffect(() => {
    if (cameraActive && !capturedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [cameraActive, capturedImage]);

  return (
    <CameraContainer>
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {cameraActive && !capturedImage && (
        <FullscreenContainer>
          <VideoElement ref={videoRef} autoPlay playsInline muted />
          <CaptureButtonContainer>
            <CircularCaptureButton
              onClick={onCapture}
              aria-label="Take photo"
            />
          </CaptureButtonContainer>
          <CloseButton onClick={onReset} aria-label="Close camera">
            ✕
          </CloseButton>
        </FullscreenContainer>
      )}

      {!cameraActive && !capturedImage && (
        <>
          <PlaceholderBox>
            <PlaceholderText>
              Camera is not active. Click below to start.
            </PlaceholderText>
          </PlaceholderBox>
          <ButtonsContainer>
            <Button onClick={onStartCamera} variant="primary" size="xl">
              Start Camera
            </Button>
            <Button
              onClick={onFileUpload}
              variant="outline"
              size="xl"
              disabled={isConverting}
            >
              {isConverting ? "Converting..." : "Upload Image"}
            </Button>
          </ButtonsContainer>
        </>
      )}

      {capturedImage && (
        <>
          <CapturedImage src={capturedImage} alt="Captured device" />
          <ButtonsContainer>
            <Button
              onClick={onAnalyze}
              variant="primary"
              size="xl"
              disabled={isConverting}
            >
              {isConverting ? "Converting..." : "Scan"}
            </Button>
            <Button onClick={onReset} variant="outline" size="xl">
              New Image
            </Button>
          </ButtonsContainer>
        </>
      )}
    </CameraContainer>
  );
};
